import { useTeamProviders } from "@redotech/redo-merchant-app-common/hooks/use-team-providers";
import { CustomerTagProvider } from "@redotech/redo-model/customer-tags/customer-tag-record";
import { Provider } from "@redotech/redo-model/order";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { TagGroup } from "@redotech/redo-model/support/tag-group-zod-schema";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { TextInput } from "@redotech/redo-web/text-input";
import { memo, useMemo } from "react";
import { PillColorPicker } from "./conversation-tag-input-color-picker";
import { ConversationTagPillThemes } from "./conversation-tag-pill";

export const TagInputFields = memo(function TagInputFields({
  name,
  setName,
  pillTheme,
  setPillTheme,
  showNameError,
  disableNameEdit = false,
  tagGroups = [],
  selectedTagGroup,
  setSelectedTagGroup,
  provider,
  setProvider,
}: {
  name: string;
  setName(name: string): void;
  showNameError: boolean;
  pillTheme: PillTheme;
  setPillTheme(pillTheme: PillTheme): void;
  disableNameEdit?: boolean;
  tagGroups?: TagGroup[];
  selectedTagGroup?: string | null;
  setSelectedTagGroup?: (tagGroupId: string | null) => void;
  provider?: CustomerTagProvider;
  setProvider?: (provider: CustomerTagProvider) => void;
}) {
  const tagGroupOptions = useMemo((): TextRedoListItem<string | null>[] => {
    const options: TextRedoListItem<string | null>[] = [
      { id: "general", type: "text" as const, text: "General", value: null },
    ];

    for (const group of tagGroups) {
      options.push({
        id: group._id,
        type: "text" as const,
        text: group.name,
        value: group._id,
      });
    }

    return options;
  }, [tagGroups]);

  const selectedTagGroupOption = useMemo(() => {
    return tagGroupOptions.find((option) => option.value === selectedTagGroup);
  }, [tagGroupOptions, selectedTagGroup]);

  const { data: teamProviders } = useTeamProviders();

  const providerOptions: TextRedoListItem<CustomerTagProvider>[] = useMemo(
    () => [
      {
        id: CustomerTagProvider.REDO,
        text: "Redo",
        type: "text" as const,
        value: CustomerTagProvider.REDO,
      },
      ...(teamProviders?.available?.includes(Provider.SHOPIFY)
        ? [
            {
              id: CustomerTagProvider.SHOPIFY,
              text: "Shopify",
              type: "text" as const,
              value: CustomerTagProvider.SHOPIFY,
            },
          ]
        : []),
      ...(teamProviders?.available?.includes(Provider.COMMENTSOLD)
        ? [
            {
              id: CustomerTagProvider.COMMENTSOLD,
              text: "Commentsold",
              type: "text" as const,
              value: CustomerTagProvider.COMMENTSOLD,
            },
          ]
        : []),
    ],
    [teamProviders],
  );

  return (
    <Flex dir="column" p="sm">
      <Flex dir="column">
        <Text fontSize="md" fontWeight="semibold">
          Tag name
        </Text>
        <TextInput disabled={disableNameEdit} onChange={setName} value={name} />
        {showNameError && (
          <Text
            fontSize="xs"
            pl="sm"
            textColor="error"
          >{`A tag with the name ${name} already exists`}</Text>
        )}
      </Flex>

      {setSelectedTagGroup && (
        <Flex dir="column" pt="md">
          <Text fontSize="md" fontWeight="semibold">
            Tag group
          </Text>
          <RedoSingleSelectDropdownInput
            options={tagGroupOptions}
            optionSelected={({ item }) => {
              if (item && "type" in item && item.type === "text") {
                setSelectedTagGroup(item.value);
              }
            }}
            placeholder="Select a group"
            selectedItem={selectedTagGroupOption}
            size="sm"
          />
        </Flex>
      )}

      {setProvider && (
        <Flex dir="column" pt="md">
          <Text fontSize="md" fontWeight="semibold">
            Tag provider
          </Text>
          <Flex dir="column" gap="sm">
            <RedoSingleSelectDropdownInput
              options={providerOptions}
              optionSelected={({ item }) => {
                setProvider(item.value);
              }}
              selectedItem={providerOptions.find(
                (option) => option.value === provider,
              )}
              size="sm"
            />
            <Text fontSize="xs" textColor="secondary">
              Select an e-commerce provider to sync the tag with. If Redo is
              selected, the tag will exist only in Redo.
            </Text>
          </Flex>
        </Flex>
      )}

      <Flex dir="column" pt="md">
        <Text fontSize="md" fontWeight="semibold">
          Tag color
        </Text>
        <PillColorPicker
          options={ConversationTagPillThemes}
          setValue={setPillTheme}
          value={pillTheme}
        />
      </Flex>
    </Flex>
  );
});
